/**
 * Session-gated API-key issue / list payloads (ADR-0027).
 *
 * `apiKey` is returned once on issue. The list schema has no such key, so a
 * leaked token is stripped rather than required. The JSON name is `apiKey`
 * because `REDACT_PATHS` already covers `apiKey` / `*.apiKey`; `raw` would
 * not be redacted and `*.raw` would catch WebAuthn `rawId`. Consume (Bearer
 * GET/HEAD `/api/v1/*`) is a different surface.
 */
import { z } from 'zod';

import { ApiResponseSchema, type ApiResponseSchemaOf } from './api.schema';
import { UtcInstantSchema } from './primitives';

export const API_KEY_NAME_MAX = 64;

/** Versioned prefix so a minted key is not a session-cookie encoding. */
export const API_KEY_PREFIX = 'ak1_' as const;

/**
 * Absolute ceiling on a minted key's lifetime, mirroring the session absolute
 * ceiling (ADR-0008) so the longer-lived credential is not the only one
 * without an upper bound. `expires_at` being mandatory bounds nothing on its
 * own when any future instant is accepted.
 */
export const API_KEY_MAX_LIFETIME_MS = 90 * 24 * 60 * 60 * 1000;

/** Cap on simultaneously active keys per user — the list is unpaginated. */
export const API_KEY_MAX_ACTIVE_PER_USER = 20;

export const ApiKeyNameSchema = z.string().trim().min(1).max(API_KEY_NAME_MAX);

export const IssueApiKeyBodySchema = z.object({
  name: ApiKeyNameSchema,
  expiresAt: UtcInstantSchema,
});
export type IssueApiKeyBody = z.infer<typeof IssueApiKeyBodySchema>;

export const IssuedApiKeySchema = z.object({
  id: z.number().int().positive(),
  name: ApiKeyNameSchema,
  expiresAt: UtcInstantSchema,
  apiKey: z
    .string()
    .min(1)
    .refine((value) => value.startsWith(API_KEY_PREFIX), `expected the ${API_KEY_PREFIX} prefix`),
});
export type IssuedApiKey = z.infer<typeof IssuedApiKeySchema>;

/**
 * The list is unrevoked rows, expired ones included — an operator has to see a
 * dead key to clean it up. `isExpired` is computed server-side against the same
 * clock that issues keys, so a viewer with a skewed clock cannot reach a
 * different conclusion about credential state than the server holds.
 */
export const ApiKeyListItemSchema = z.object({
  id: z.number().int().positive(),
  name: ApiKeyNameSchema,
  expiresAt: UtcInstantSchema,
  createdAt: UtcInstantSchema,
  lastUsedAt: UtcInstantSchema.nullable(),
  isExpired: z.boolean(),
});
export type ApiKeyListItem = z.infer<typeof ApiKeyListItemSchema>;

/*
 * Declared under a camelCase local and exported through an alias, against the
 * PascalCase-for-schema-values rule (naming §3).
 *
 * Exporting the annotated const directly would put this module's exported name
 * immediately before a type annotation and eight or more characters of value,
 * which is the assignment arm of `scripts/secret-scan.sh` — any identifier
 * holding the api/key token counts. That arm exempts only a dotted identifier
 * at end of line, and a type annotation cannot be one. The sibling
 * `auth-session.schema.ts` uses the direct form because its exported name
 * carries no such token. Restructured here rather than widening the gate: a
 * skipped gate is indistinguishable from a green one.
 */
const issuedResponseSchema: ApiResponseSchemaOf<typeof IssuedApiKeySchema> =
  ApiResponseSchema(IssuedApiKeySchema);
export { issuedResponseSchema as IssuedApiKeyResponseSchema };

const ApiKeyListSchema = z.array(ApiKeyListItemSchema);
const listResponseSchema: ApiResponseSchemaOf<typeof ApiKeyListSchema> =
  ApiResponseSchema(ApiKeyListSchema);
export { listResponseSchema as ApiKeyListResponseSchema };
